import {
  ArrowDown01,
  ArrowDownAZ,
  ArrowUp01,
  ArrowUpAZ,
  Check,
  ChevronDown,
  Filter,
  Search,
  X,
} from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';

type SortDirection = 'asc' | 'desc';
type SortVariant = 'alpha' | 'numeric';

interface SearchField {
  readonly type: 'search';
  readonly id: string;
  readonly label: string;
  readonly placeholder: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  readonly narrow?: boolean;
}

interface SelectOption {
  readonly value: string;
  readonly label: string;
}

interface SelectField {
  readonly type: 'select';
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly options: readonly SelectOption[];
  readonly onChange: (value: string) => void;
  readonly narrow?: boolean;
}

interface DateField {
  readonly type: 'date';
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly narrow?: boolean;
}

interface SortButton {
  readonly field: string;
  readonly label: string;
  readonly variant?: SortVariant;
}

interface SortConfig {
  readonly type: 'sort';
  readonly activeField: string;
  readonly direction: SortDirection;
  readonly buttons: readonly SortButton[];
  readonly onToggle: (field: string) => void;
}

interface CustomSlot {
  readonly type: 'custom';
  readonly content: ReactNode;
}

type FilterItem = SearchField | SelectField | DateField | SortConfig | CustomSlot;

interface FilterRow {
  readonly items: readonly FilterItem[];
}

interface TableFiltersProps {
  readonly rows: readonly FilterRow[];
  readonly compact?: boolean;
  readonly onApply?: () => void;
  readonly onClear?: () => void;
  readonly showClear?: boolean;
  readonly applyLabel?: string;
}

function renderSortIcon(
  isActive: boolean,
  direction: SortDirection,
  variant: SortVariant,
): ReactNode {
  if (!isActive) {
    return variant === 'numeric' ? <ArrowDown01 size={14} /> : <ArrowDownAZ size={14} />;
  }
  if (variant === 'numeric') {
    return direction === 'asc' ? <ArrowDown01 size={14} /> : <ArrowUp01 size={14} />;
  }
  return direction === 'asc' ? <ArrowDownAZ size={14} /> : <ArrowUpAZ size={14} />;
}

function CustomSelect({ item }: { item: SelectField }): React.JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selectedLabel = item.options.find((o) => o.value === item.value)?.label ?? '';

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  return (
    <div
      ref={ref}
      className={`audit-filters__field${item.narrow ? ' audit-filters__field--narrow' : ''}`}
    >
      <label className="audit-filters__label">{item.label}</label>
      <div className="audit-filters__dropdown">
        <button
          type="button"
          className="audit-filters__dropdown-trigger"
          onClick={() => setIsOpen((prev) => !prev)}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <span className="audit-filters__dropdown-value">{selectedLabel}</span>
          <ChevronDown size={14} className="audit-filters__dropdown-chevron" />
        </button>
        {isOpen && (
          <ul className="audit-filters__dropdown-menu" role="listbox">
            {item.options.map((opt) => (
              <li key={opt.value} role="option" aria-selected={opt.value === item.value}>
                <button
                  type="button"
                  className={`audit-filters__dropdown-item${opt.value === item.value ? ' audit-filters__dropdown-item--active' : ''}`}
                  onClick={() => {
                    item.onChange(opt.value);
                    setIsOpen(false);
                  }}
                >
                  <span>{opt.label}</span>
                  {opt.value === item.value && <Check size={14} />}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function renderItem(item: FilterItem, index: number): ReactNode {
  switch (item.type) {
    case 'search':
      return (
        <div
          key={item.id}
          className={`audit-filters__field${item.narrow ? ' audit-filters__field--narrow' : ''}`}
        >
          <label className="audit-filters__label" htmlFor={item.id}>
            {item.label}
          </label>
          <div className="audit-filters__input-wrapper">
            <Search size={14} className="audit-filters__input-icon" />
            <input
              id={item.id}
              type="text"
              className="audit-filters__input"
              placeholder={item.placeholder}
              value={item.value}
              onChange={(e) => item.onChange(e.target.value)}
              onKeyDown={item.onKeyDown}
            />
          </div>
        </div>
      );

    case 'select':
      return <CustomSelect key={item.id} item={item} />;

    case 'date':
      return (
        <div
          key={item.id}
          className={`audit-filters__field${item.narrow ? ' audit-filters__field--narrow' : ''}`}
        >
          <label className="audit-filters__label" htmlFor={item.id}>
            {item.label}
          </label>
          <input
            id={item.id}
            type="date"
            className="audit-filters__input"
            value={item.value}
            onChange={(e) => item.onChange(e.target.value)}
          />
        </div>
      );

    case 'sort':
      return (
        <div key={`sort-${index}`} className="audit-filters__sort-group">
          {item.buttons.map((btn) => {
            const isActive = item.activeField === btn.field;
            const variant = btn.variant ?? 'alpha';
            return (
              <button
                key={btn.field}
                type="button"
                className={`btn btn-sm ${isActive ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => item.onToggle(btn.field)}
                title={`Sort by ${btn.label.toLowerCase()}`}
              >
                {renderSortIcon(isActive, item.direction, variant)}
                {btn.label}
              </button>
            );
          })}
        </div>
      );

    case 'custom':
      return <div key={`custom-${index}`}>{item.content}</div>;
  }
}

function isRightAligned(item: FilterItem): boolean {
  return item.type === 'sort' || item.type === 'custom';
}

export default function TableFilters({
  rows,
  compact = false,
  onApply,
  onClear,
  showClear = false,
  applyLabel = 'Apply filters',
}: TableFiltersProps): React.JSX.Element {
  const hasActions = onApply || showClear;

  return (
    <div className={`audit-filters${compact ? ' audit-filters--compact' : ''}`}>
      {rows.map((row, rowIndex) => {
        const leftItems = row.items.filter((item) => !isRightAligned(item));
        const rightItems = row.items.filter(isRightAligned);
        const showActionsHere = rowIndex === rows.length - 1 && hasActions;

        return (
          <div key={rowIndex} className="audit-filters__row">
            {leftItems.map((item, itemIndex) => renderItem(item, itemIndex))}

            {(rightItems.length > 0 || showActionsHere) && (
              <div className="audit-filters__right">
                {rightItems.map((item, itemIndex) => renderItem(item, itemIndex))}

                {showActionsHere && (
                  <div className="audit-filters__actions">
                    {onApply && (
                      <button type="button" className="btn btn-primary btn-sm" onClick={onApply}>
                        <Filter size={14} />
                        {applyLabel}
                      </button>
                    )}
                    {showClear && onClear && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={onClear}
                        title="Clear filters"
                      >
                        <X size={14} />
                        Clear
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export type {
  CustomSlot,
  DateField,
  FilterItem,
  FilterRow,
  SearchField,
  SelectField,
  SelectOption,
  SortButton,
  SortConfig,
  SortDirection,
  SortVariant,
  TableFiltersProps,
};
